/**
 * Thin fetch wrapper for the app's own backend (/api/*). All calls are
 * same-origin in dev thanks to Vite's proxy; in prod the app is served from
 * the same host as its API, so credentials work without CORS contortions.
 *
 * 401 → bounce to /oauth/start to re-authenticate.
 */

import { useWizardStore } from "@/state/wizard-store";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly issues: string[] | undefined;
  constructor(status: number, body: { error: string; message: string; issues?: string[] }) {
    super(body.message);
    this.status = status;
    this.code = body.error;
    this.issues = body.issues;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // An account can have more than one site; tell the server which one this
  // page is working on. The server checks the account owns it.
  const currentSiteId = useWizardStore.getState().currentSiteId;
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(currentSiteId ? { "X-Rumio-Site": currentSiteId } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    // Force re-auth. Browser navigates to /oauth/start which bounces to
    // rumio.world for consent, then back to /oauth/callback.
    window.location.href = `/oauth/start?return_to=${encodeURIComponent(
      window.location.pathname + window.location.search,
    )}`;
    // Resolve with a never-settling promise so callers don't render error
    // states for the moment between this 401 and the redirect.
    return new Promise(() => {});
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, body);
  }
  return body as T;
}

/** What we recover from a public Airbnb listing (all optional — may be sparse). */
export interface AirbnbListing {
  sourceUrl?: string;
  airbnbId?: string;
  title?: string;
  description?: string;
  propertyTypeRaw?: string;
  maxGuests?: number;
  bedrooms?: number;
  beds?: number;
  bathrooms?: number;
  amenities?: string[];
  houseRules?: string[];
  city?: string;
  region?: string;
  country?: string;
  lat?: number;
  lng?: number;
  nightlyPrice?: number;
  currency?: string;
  rating?: number;
  reviewCount?: number;
  photoUrls?: string[];
  hostName?: string;
}

/**
 * A sellable add-on from the platform catalog (`/api/billing/plans`). Absent
 * entirely until the platform's bundle work ships — every consumer must treat
 * a missing catalog as "no add-ons" and render the pre-bundle UI.
 *
 * `intro_*` drive the promo badge. Never hardcode the percentage or duration —
 * they come from the catalog so pricing changes need no app deploy.
 */
export interface Addon {
  slug: string;
  name: string;
  description: string | null;
  monthly_price_cents: number;
  annual_price_cents: number;
  intro_percent_off?: number | null;
  intro_duration_months?: number | null;
  /**
   * Groups tiers of one product (e.g. eTaxEngine Standard vs Scale) so the
   * picker offers exactly one of them. Absent on older platforms — treat
   * each add-on as its own family then.
   */
  product_family?: string | null;
}

/** An add-on the customer actually bought (from `/api/billing/subscription`). */
/** Terms of a platform promo code the platform is willing to honour. */
export interface PromoTerms {
  valid: true;
  code: string;
  percent_off: number;
  duration_months: number;
  /** plan_only: the hosting plan; plan_and_addons: also stacks on add-on lines. */
  applies_to: "plan_only" | "plan_and_addons";
}

export interface SubscriptionAddon {
  slug: string;
  name: string;
  status: string;
  intro_percent_off?: number | null;
  intro_ends_at?: string | null;
}

export interface ManualReviewInput {
  guest_name: string;
  rating: number;
  body?: string | null;
  review_date?: string | null;
}

export const api = {
  me: () => request<{ user_id: string; email: string; installation_id: string }>("/api/me"),
  submitQuestionnaire: (body: unknown) =>
    request<{ job_id: string; site_id: string }>("/api/questionnaire/submit", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  provisioningStatus: (jobId: string) =>
    request<{
      job_id: string;
      status: string;
      progress: Array<{ step: string; status: string; message: string }>;
      result: {
        siteId?: string;
        domain?: string;
        apiDomain?: string;
        manualSteps?: string[];
      } | null;
      error: string | null;
    }>(`/api/provisioning/status/${jobId}`),
  retry: (jobId: string) =>
    request<{ job_id: string }>(`/api/provisioning/retry/${jobId}`, { method: "POST" }),
  customDomainPreflight: (domain: string) =>
    request<{
      domain: string;
      status: "in_account" | "subdomain_ok" | "not_in_account" | "unregistered" | "cf_unconfigured" | "invalid_domain";
      zone_status?: string;
      authoritative_ns?: string[];
      recommendation: string;
    }>("/api/custom-domain/preflight", { method: "POST", body: JSON.stringify({ domain }) }),
  promoteDomain: (newDomain: string) =>
    request<{ job_id: string }>("/api/custom-domain/promote", {
      method: "POST",
      body: JSON.stringify({ newDomain }),
    }),
  customDomainStatus: (jobId: string) =>
    request<{
      job_id: string;
      status: string;
      progress: Array<{ step: string; status: string; message: string }>;
      result: {
        siteId?: string;
        oldDomain?: string;
        newDomain?: string;
        newApiDomain?: string;
        nameservers?: string[];
        manualSteps?: string[];
      } | null;
      error: string | null;
    }>(`/api/custom-domain/status/${jobId}`),
  retryCustomDomain: (jobId: string) =>
    request<{ job_id: string }>(`/api/custom-domain/retry/${jobId}`, { method: "POST" }),
  // ─── Import from Airbnb (lodging) ───
  previewAirbnbListing: (input: { url?: string; html?: string }) =>
    request<{
      listing: AirbnbListing | null;
      source: "paste" | "fetch";
      blocked: boolean;
      error: string | null;
    }>("/api/listings/import-airbnb/preview", { method: "POST", body: JSON.stringify(input) }),
  importAirbnbListing: (input: { url?: string; html?: string }) =>
    request<{
      ok: boolean;
      property_id?: string;
      slug?: string;
      photos_imported?: number;
      amenities_imported?: number;
      listing?: AirbnbListing;
      blocked?: boolean;
      error?: string;
    }>("/api/listings/import-airbnb", { method: "POST", body: JSON.stringify(input) }),
  connectAirbnbCalendar: (propertyId: string, icalUrl: string) =>
    request<{
      ok: boolean;
      feed_id: string;
      synced?: { imported: number; removed: number; conflicts: number } | null;
      warning?: string;
    }>("/api/listings/connect-calendar", {
      method: "POST",
      body: JSON.stringify({ property_id: propertyId, ical_url: icalUrl }),
    }),
  generateReviewImportToken: () =>
    request<{ token: string; api_base_url: string; ingest_url: string }>("/api/reviews/import-token", {
      method: "POST",
    }),
  importReviews: (propertyId: string, reviews: ManualReviewInput[]) =>
    request<{ imported: number; skipped: number }>("/api/reviews/import", {
      method: "POST",
      body: JSON.stringify({ property_id: propertyId, reviews }),
    }),
  checkout: (plan: string, addons?: string[], billingInterval?: "monthly" | "annual", promoCode?: string) =>
    request<{ checkout_url: string; mock?: boolean }>("/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({
        plan,
        ...(addons?.length ? { addons } : {}),
        // Sent only for annual, so the monthly request body is byte-identical
        // to what it was before annual existed.
        ...(billingInterval === "annual" ? { billing_interval: "annual" } : {}),
        // Likewise only when the customer applied a code.
        ...(promoCode ? { promo_code: promoCode } : {}),
      }),
    }),
  /** Check a platform promo code; rejects with ApiError (`invalid_promo`) when it can't be used. */
  validatePromo: (code: string) =>
    request<PromoTerms>("/api/billing/promo/validate", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  /** Stripe's hosted portal: card, invoices, plan switch, cancellation. */
  billingPortal: (returnPath = "/subscription") =>
    request<{ portal_url: string }>("/api/billing/portal", {
      method: "POST",
      body: JSON.stringify({ return_path: returnPath }),
    }),
  subscription: () =>
    request<{
      data: {
        id: string;
        plan_slug: string;
        status: string;
        trial_ends_at: string | null;
        current_period_end: string | null;
        cancel_at_period_end: boolean;
        suspended_at: string | null;
        grace_period_ends_at: string | null;
        tenant_site_id: string | null;
        domain?: string | null;
        addons?: SubscriptionAddon[];
        promo?: { code: string; percent_off: number | null } | null;
      } | null;
      /** Every built site on the account; more than one shows a switcher. */
      sites?: Array<{
        tenant_site_id: string;
        domain: string | null;
        plan_slug: string;
        status: string;
      }>;
    }>("/api/billing/subscription"),
  plans: () =>
    request<{
      plans: Array<{
        slug: string;
        name: string;
        description: string | null;
        tier_level: number;
        monthly_price_cents: number;
        annual_price_cents: number;
        features: Record<string, unknown>;
      }>;
      addons?: Addon[];
    }>("/api/billing/plans"),
};
